import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import deploy1 from '../../assets/Deploy/deploy1.png'
import deploy2 from '../../assets/Deploy/deploy2.png'
import login from '../../assets/Deploy/login.png'
import PageNavigation from '../../components/PageNavigation/PageNavigation'
import './QuickStart.css'
import '../PlatformCapabilities/Apps/Apps.css'

const QuickStart = () => {
  const [activeStep, setActiveStep] = useState(1)
  const [activeSection, setActiveSection] = useState('getting-started')
  const [lightboxImage, setLightboxImage] = useState(null)
  const [lightboxAlt, setLightboxAlt] = useState('')

  const openLightbox = (imageSrc, altText) => {
    setLightboxImage(imageSrc)
    setLightboxAlt(altText)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxImage(null)
    setLightboxAlt('')
    document.body.style.overflow = ''
  }

  // Close lightbox on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox()
      }
    }
    if (lightboxImage) {
      window.addEventListener('keydown', handleEscape)
    }
    return () => {
      window.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [lightboxImage])

  const sections = [
    { id: 'getting-started', title: 'Getting Started' },
    { id: 'deploy', title: 'Deploy Your Application' },
    { id: 'resources', title: 'Additional Resources' }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(`quickstart-${sections[i].id}`)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  const initialSteps = [
    {
      id: 1,
      title: 'Sign up',
      description: 'Create your Aedify.AI account to get started with deploying applications.',
      image: login,
      details: 'If you\'re already signed up, you can skip ahead to Step 2. If you haven\'t signed up yet, you can sign up for free at Aedify.AI. Sign up with your email or Google account. For information about what\'s included with the Free and paid plans, see our pricing page.',
      isSignUp: true
    },
    {
      id: 2,
      title: 'Open Dashboard and start deploying',
      description: 'After logging in, navigate to the Dashboard and select your preferred deployment method.',
      images: [deploy1, deploy2],
      details: 'From the Dashboard, click "Deploy App", then you can choose to deploy from GitHub or deploy from a container image.'
    }
  ]

  return (
    <div className="quick-start-page">
      <div className="quick-start-content-wrapper">
        <div className="quick-start-main-content">
      <h1>Quick Start</h1>
      <p className="intro">
            Get started with Aedify.AI in minutes. Follow these steps to create your account and deploy your first application.
      </p>

          {/* Getting Started Steps */}
          <section id="quickstart-getting-started" className="quick-start-section deployment-section">
            <h2>Getting Started</h2>
            <p className="section-intro">
              Follow these initial steps to set up your account and access the deployment dashboard.
            </p>

            {initialSteps.map((step) => (
              <div 
                key={step.id} 
                className={`step-container ${activeStep === step.id ? 'active' : ''} ${step.isSignUp ? 'signup-step' : ''}`}
                id={`getting-started-step-${step.id}`}
              >
                <div className="step-header">
                  <div className="step-number">{step.id}</div>
                  <h3>{step.title}</h3>
                </div>
                {step.images && step.images.length > 0 && (
                  <div className="step-images-row">
                    {step.images.map((img, idx) => (
                      <div key={idx} className="step-image">
                        <img 
                          src={img} 
                          alt={`Step ${step.id}: ${step.title} - Image ${idx + 1}`}
                          onClick={() => openLightbox(img, `Step ${step.id}: ${step.title} - Image ${idx + 1}`)}
                          className="clickable-image"
                        />
                      </div>
                    ))}
                  </div>
                )}
                {step.isSignUp ? (
                  <div className="signup-step-content">
                    <div className="signup-content">
                      <p className="step-description">{step.description}</p>
                      <p>
                        If you're already signed up, you can skip ahead to{' '}
                        <a href="#getting-started-step-2" className="skip-link">Step 2</a>.
                      </p>
                      <p>
                        If you haven't signed up yet, you can sign up for free here:{' '}
                        <a href="https://aedify.ai" target="_blank" rel="noopener noreferrer" className="signup-link">
                          https://aedify.ai
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.07102 11.3496L0.963318 10.2419L9.2017 1.98864H2.83807L2.85227 0.454545H11.8438V9.46023H10.2955L10.3097 3.09659L2.07102 11.3496Z" fill="currentColor"/>
                          </svg>
                        </a>
                      </p>
                      <p>Sign up with your email or Google account.</p>
                      <p>
                        For information about what's included with the Free and paid plans, see{' '}
                        <a href="https://aedify.ai/pricing" target="_blank" rel="noopener noreferrer" className="pricing-link">Aedify pricing</a>.
                      </p>
                    </div>
                    {step.image && (
                      <div className="signup-image-wrapper">
                        <div className="step-image signup-image">
                          <img 
                            src={step.image} 
                            alt={`Step ${step.id}: ${step.title}`}
                            onClick={() => openLightbox(step.image, `Step ${step.id}: ${step.title}`)}
                            className="clickable-image"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="step-details">
                    <p>{step.details}</p>
                  </div>
                )}
              </div>
            ))}
      </section>

          <section id="quickstart-deploy" className="quick-start-section">
            <h2>Next: Deploy Your Application</h2>
        <p>
              Now that you're signed up and have accessed the Dashboard, you're ready to deploy your first application. 
              Aedify.AI supports two deployment methods:
        </p>
        <h3>Deploy from GitHub</h3>
            <p>
              Deploy applications directly from GitHub repositories. You can use a public repository URL or connect 
              your GitHub account to access private repositories and enable automatic deployments.
            </p>
        <h3>Deploy from Image</h3>
            <p>
              Deploy pre-built container images from any container registry, including Docker Hub, GitHub Container 
              Registry, or your own private registry.
            </p>
        <p>
              For detailed step-by-step instructions with images, see the{' '}
              <Link to="/platform-capabilities/apps">Apps documentation</Link>.
        </p>
      </section>

          <section id="quickstart-resources" className="quick-start-section">
            <h2>Additional Resources</h2>
        <p>
              Once you've deployed your first application, explore more platform capabilities:
        </p>
        <ul>
              <li><Link to="/platform-capabilities/registry">Container Registry</Link> - Manage and store your Docker images</li>
          <li><Link to="/platform-capabilities/database">Database</Link> - Managed database instances</li>
              {/* <li><Link to="/platform-capabilities/cloud-storage">Cloud Storage</Link> - Object storage for files</li> */} {/* Hidden for now */}
              <li><Link to="/platform-capabilities/vps">VPS</Link> - Deploy and manage virtual private servers</li>
              <li><Link to="/account/billing">Billing and Usage</Link> - Manage your account and monitor usage</li>
        </ul>
      </section>

          {/* Lightbox Modal */}
          {lightboxImage && (
            <div className="lightbox-overlay" onClick={closeLightbox}>
              <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <img src={lightboxImage} alt={lightboxAlt} className="lightbox-image" />
              </div>
            </div>
          )}

          <PageNavigation />
        </div>

        {/* Right Sidebar - Section Navigation */}
        <aside className="quick-start-sidebar">
          <div className="sidebar-section">
            <h3>Contents</h3>
            <nav className="section-nav">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#quickstart-${section.id}`}
                  className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveSection(section.id)
                    document.getElementById(`quickstart-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default QuickStart
